import { format } from 'date-fns';
import { taskAPI } from '@/utils/api';

/** 同类型任务列表请求合并（仅显式需要服务端去重时使用） */
const inflightLoads = new Map();

function loadExistingTasks(type) {
  if (!inflightLoads.has(type)) {
    const p = taskAPI
      .getAll({ type, pageSize: 200, summary: true })
      .then(({ tasks }) => tasks || [])
      .catch((error) => {
        console.error('加载任务列表失败:', error);
        return [];
      })
      .finally(() => {
        inflightLoads.delete(type);
      });
    inflightLoads.set(type, p);
  }
  return inflightLoads.get(type);
}

/**
 * 万能改图对外只展示/检索批次号（I2I-…）；旧任务 id 如 img2img e-123 不再作为批次展示。
 */
export function resolveImg2ImgBatchNo(task) {
  const sub = String(task?.subAccount || '').trim();
  if (sub) return sub;
  const id = String(task?.id || task?.taskId || '').trim();
  if (id.startsWith('I2I-')) return id;
  return '';
}

/** AI 文案对外展示/检索批次号（COPY-…） */
export function resolveAiCopywritingBatchNo(task) {
  const sub = String(task?.subAccount || '').trim();
  if (sub) return sub;
  const id = String(task?.id || task?.taskId || '').trim();
  if (id.startsWith('COPY-')) return id;
  return '';
}

function resolvePrefixedBatchNo(task, prefix) {
  const sub = String(task?.subAccount || '').trim();
  if (sub) return sub;
  const id = String(task?.id || task?.taskId || '').trim();
  if (id.startsWith(`${prefix}-`)) return id;
  return '';
}

/** AI 文生图批次号（T2I-…） */
export function resolveAiTextToImageBatchNo(task) {
  return resolvePrefixedBatchNo(task, 'T2I');
}

/** 图生视频批次号（VID-…） */
export function resolveVideosBatchNo(task) {
  const sub = String(task?.subAccount || '').trim();
  if (sub) return sub;
  const id = String(task?.id || task?.taskId || '').trim();
  if (id.startsWith('VID-') || id.startsWith('T2V-')) return id;
  return id;
}

/** 文生视频批次号（T2V-…） */
export function resolveTextToVideoBatchNo(task) {
  return resolveVideosBatchNo(task);
}

/** AI 扩图批次号（OUTPAINT-…） */
export function resolveAiOutpaintBatchNo(task) {
  return resolvePrefixedBatchNo(task, 'OUTPAINT');
}

/** 图案裁剪批次号（CROP-…） */
export function resolveImageCropBatchNo(task) {
  return resolvePrefixedBatchNo(task, 'CROP');
}

/** 无损放大批次号（ENH-…） */
export function resolveImageEnhanceBatchNo(task) {
  return resolvePrefixedBatchNo(task, 'ENH');
}

/** 智能抠图批次号（CUT-…） */
export function resolveSmartCutoutBatchNo(task) {
  return resolvePrefixedBatchNo(task, 'CUT');
}

/** 印花提取批次号（STAMP-…） */
export function resolveStampExtractBatchNo(task) {
  return resolvePrefixedBatchNo(task, 'STAMP');
}

/** 模特试衣批次号（TRYON-…） */
export function resolveModelTryOnBatchNo(task) {
  return resolvePrefixedBatchNo(task, 'TRYON');
}

/** 手持商品批次号（HANDHELD-…） */
export function resolveModelHandheldBatchNo(task) {
  return resolvePrefixedBatchNo(task, 'HANDHELD');
}

/** 姿势裂变批次号（POSE-…） */
export function resolveModelPoseFissionBatchNo(task) {
  return resolvePrefixedBatchNo(task, 'POSE');
}

/** 万物穿戴批次号（WEAR-…） */
export function resolveModelUniversalWearBatchNo(task) {
  return resolvePrefixedBatchNo(task, 'WEAR');
}

/**
 * 本地生成任务编号与次号（不请求网络，创建弹窗应立即返回）。
 */
const BATCH_ID_TYPES = {
  img2img: 'I2I',
  aiCopywriting: 'COPY',
  aiTextToImage: 'T2I',
  aiOutpaint: 'OUTPAINT',
  imageCrop: 'CROP',
  modelTryOn: 'TRYON',
  modelHandheld: 'HANDHELD',
  modelPoseFission: 'POSE',
  modelUniversalWear: 'WEAR',
  /** 与批次号一致，避免 taskId 含空格导致落盘目录与签名归属对不上 */
  videos: 'VID',
  textToVideo: 'T2V',
};

export function allocateTaskIdentifiersLocal(type) {
  const batchPrefix = BATCH_ID_TYPES[type];
  if (batchPrefix) {
    const batchNo = buildPrefixedBatchSubAccount(batchPrefix, []);
    return { taskId: batchNo, subAccount: batchNo };
  }
  return {
    taskId: buildTaskId(type, []),
    subAccount: buildSubAccount(type, []),
  };
}

const SERVER_ID_LOOKUP_TIMEOUT_MS = 2500;

/**
 * 创建任务时分配编号：默认本地生成；仅在需要时短时查询服务端去重。
 */
export async function allocateTaskIdentifiers(type, { checkServer = false } = {}) {
  if (!checkServer) {
    return allocateTaskIdentifiersLocal(type);
  }
  try {
    const existingTasks = await Promise.race([
      loadExistingTasks(type),
      new Promise((resolve) => {
        setTimeout(() => resolve(null), SERVER_ID_LOOKUP_TIMEOUT_MS);
      }),
    ]);
    if (Array.isArray(existingTasks)) {
      const subAccount = buildSubAccount(type, existingTasks);
      if (BATCH_ID_TYPES[type]) {
        return { taskId: subAccount, subAccount };
      }
      return {
        taskId: buildTaskId(type, existingTasks),
        subAccount,
      };
    }
  } catch {
    /* fallback */
  }
  return allocateTaskIdentifiersLocal(type);
}

/**
 * @param {Array} [existingTasks] - 已加载的同类型任务，避免重复请求
 */
export async function generateTaskId(type, existingTasks) {
  const tasks = existingTasks ?? (await loadExistingTasks(type));
  return buildTaskId(type, tasks);
}

/**
 * @param {Array} [existingTasks] - 已加载的同类型任务，避免重复请求
 */
export async function generateSubAccount(type, existingTasks) {
  const tasks = existingTasks ?? (await loadExistingTasks(type));
  return buildSubAccount(type, tasks);
}

function buildTaskId(type, allTasks) {
  const existingIds = (allTasks || [])
    .map((t) => t.id)
    .filter((id) => id && id.startsWith(type));

  const usedCombinations = new Set();
  existingIds.forEach((id) => {
    const match = id.match(new RegExp(`^${type}\\s+([A-Za-z])-(\\d+)$`));
    if (match) {
      usedCombinations.add(`${match[1]}-${match[2]}`);
    }
  });

  const allLetters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';
  const maxAttempts = 1000;
  let attempts = 0;

  while (attempts < maxAttempts) {
    const letter = allLetters[Math.floor(Math.random() * allLetters.length)];
    const number = Math.floor(Math.random() * 9999) + 1;
    const combination = `${letter}-${number}`;
    if (!usedCombinations.has(combination)) {
      return `${type} ${letter}-${number}`;
    }
    attempts++;
  }

  const timestamp = Date.now();
  const fallbackLetter = allLetters[timestamp % allLetters.length];
  const fallbackNumber = (timestamp % 9999) + 1;
  return `${type} ${fallbackLetter}-${fallbackNumber}`;
}

function buildSubAccount(type, allTasks) {
  const batchPrefix = BATCH_ID_TYPES[type];
  if (batchPrefix) {
    return buildPrefixedBatchSubAccount(batchPrefix, allTasks);
  }

  const typeMap = {
    videos: 'VID',
    textToVideo: 'T2V',
    imageCrop: 'CROP',
    imageEnhance: 'ENH',
    stampExtract: 'STAMP',
    smartCutout: 'CUT',
    img2img: 'I2I',
    goods: 'GOODS',
    titles: 'TITLES',
  };

  const prefix = typeMap[type] || 'TASK';
  const dateStr = format(new Date(), 'yyyyMMdd');

  const usedNumbers = new Set();
  (allTasks || []).forEach((task) => {
    if (task.subAccount) {
      const match = task.subAccount.match(new RegExp(`^${prefix}-${dateStr}-(\\d{4})$`));
      if (match) {
        usedNumbers.add(parseInt(match[1], 10));
      }
    }
  });

  const maxAttempts = 1000;
  let attempts = 0;

  while (attempts < maxAttempts) {
    const randomNum = Math.floor(1000 + Math.random() * 9000);
    if (!usedNumbers.has(randomNum)) {
      return `${prefix}-${dateStr}-${randomNum}`;
    }
    attempts++;
  }

  const fallbackNum = (Date.now() % 9000) + 1000;
  return `${prefix}-${dateStr}-${fallbackNum}`;
}

/** 批次号：{PREFIX}-年月日 + 4 位时间片 + 4 位随机，降低同秒撞库概率 */
function buildPrefixedBatchSubAccount(prefix, allTasks) {
  const dateStr = format(new Date(), 'yyyyMMdd');
  const used = new Set();
  (allTasks || []).forEach((task) => {
    [task?.subAccount, task?.id].forEach((value) => {
      if (typeof value === 'string' && value.startsWith(`${prefix}-`)) {
        used.add(value);
      }
    });
  });

  const baseTime = Date.now();
  for (let offset = 0; offset < 200; offset += 1) {
    const msPart = String((baseTime + offset) % 10000).padStart(4, '0');
    const randPart = Math.random().toString(36).slice(2, 6);
    const candidate = `${prefix}-${dateStr}${msPart}${randPart}`;
    if (!used.has(candidate)) {
      return candidate;
    }
  }

  const fallbackMs = String(baseTime).slice(-6);
  const fallbackRand = Math.random().toString(36).slice(2, 6);
  return `${prefix}-${dateStr}${fallbackMs}${fallbackRand}`;
}

/** 本地生成万能改图批次号（不请求网络）。 */
export function allocateImg2ImgBatchLocal(existingTasks = []) {
  return buildPrefixedBatchSubAccount('I2I', existingTasks);
}
